/**
 * PDV State Management
 * Manages the sale state: items, totals, customer, discount.
 * Persists to backend via API calls (optimistic local updates + backend sync).
 */
import { useReducer, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

export interface SaleItem {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  unit: string;
  subtotal: number;
  original_price?: number;
  has_promotion?: boolean;
}

export interface PdvState {
  saleId: string | null;
  items: SaleItem[];
  subtotal: number;
  discount: number;
  total: number;
  customer: { id: string; name: string } | null;
  itemCount: number;
  isLoading: boolean;
}

type PdvAction =
  | { type: 'SET_SALE'; saleId: string }
  | { type: 'ADD_ITEM'; item: SaleItem }
  | { type: 'UPDATE_ITEM'; itemId: string; newItem: SaleItem }
  | { type: 'UPDATE_ITEM_QUANTITY'; itemId: string; quantity: number }
  | { type: 'REMOVE_ITEM'; itemId: string }
  | { type: 'SET_DISCOUNT'; discount: number }
  | { type: 'SET_CUSTOMER'; customer: { id: string; name: string } | null }
  | { type: 'CLEAR_SALE' }
  | { type: 'LOAD_SALE'; state: Partial<PdvState> }
  | { type: 'SET_LOADING'; loading: boolean };

function calculateTotals(items: SaleItem[], discount: number) {
  const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
  const total = Math.max(0, Math.round((subtotal - discount) * 100) / 100);
  return { subtotal: Math.round(subtotal * 100) / 100, total, itemCount: items.length };
}

function pdvReducer(state: PdvState, action: PdvAction): PdvState {
  switch (action.type) {
    case 'SET_SALE':
      return { ...state, saleId: action.saleId };

    case 'ADD_ITEM': {
      // Check if product already exists
      const existingIndex = state.items.findIndex(
        (i) => i.product_id === action.item.product_id
      );

      let newItems: SaleItem[];
      if (existingIndex >= 0) {
        newItems = state.items.map((item, idx) => {
          if (idx === existingIndex) {
            const newQty = item.quantity + action.item.quantity;
            return {
              ...item,
              quantity: newQty,
              subtotal: Math.round(newQty * item.unit_price * 100) / 100,
            };
          }
          return item;
        });
      } else {
        newItems = [...state.items, action.item];
      }

      const addTotals = calculateTotals(newItems, state.discount);
      return { ...state, items: newItems, ...addTotals };
    }

    case 'UPDATE_ITEM': {
      const updatedItems = state.items.map((item) => {
        if (item.id === action.itemId) {
          return action.newItem;
        }
        return item;
      });
      const updateTotals = calculateTotals(updatedItems, state.discount);
      return { ...state, items: updatedItems, ...updateTotals };
    }

    case 'UPDATE_ITEM_QUANTITY': {
      const updatedItems = state.items.map((item) => {
        if (item.id === action.itemId) {
          const newSubtotal = Math.round(action.quantity * item.unit_price * 100) / 100;
          return { ...item, quantity: action.quantity, subtotal: newSubtotal };
        }
        return item;
      });
      const updateTotals = calculateTotals(updatedItems, state.discount);
      return { ...state, items: updatedItems, ...updateTotals };
    }

    case 'REMOVE_ITEM': {
      const filteredItems = state.items.filter((item) => item.id !== action.itemId);
      const removeTotals = calculateTotals(filteredItems, state.discount);
      return { ...state, items: filteredItems, ...removeTotals };
    }

    case 'SET_DISCOUNT': {
      const discTotals = calculateTotals(state.items, action.discount);
      return { ...state, discount: action.discount, ...discTotals };
    }

    case 'SET_CUSTOMER':
      return { ...state, customer: action.customer };

    case 'CLEAR_SALE':
      return initialState;

    case 'LOAD_SALE': {
      const loaded = { ...state, ...action.state };
      const loadTotals = calculateTotals(loaded.items, loaded.discount);
      return { ...loaded, ...loadTotals };
    }

    case 'SET_LOADING':
      return { ...state, isLoading: action.loading };

    default:
      return state;
  }
}

const initialState: PdvState = {
  saleId: null,
  items: [],
  subtotal: 0,
  discount: 0,
  total: 0,
  customer: null,
  itemCount: 0,
  isLoading: false,
};

export function usePdvState() {
  const [state, dispatch] = useReducer(pdvReducer, initialState);
  const saleIdRef = useRef<string | null>(null);

  // Keep ref in sync with state for use in async callbacks
  const updateSaleIdRef = (id: string | null) => {
    saleIdRef.current = id;
  };

  /**
   * Create a new sale in the backend.
   * Returns the sale ID.
   */
  const createSale = useCallback(async (): Promise<string | null> => {
    try {
      const response = await apiFetch('/api/sales', { method: 'POST', body: JSON.stringify({}) });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Erro ao criar venda' }));
        toast.error(err.error || 'Erro ao criar venda no servidor');
        return null;
      }
      const sale = await response.json();
      dispatch({ type: 'SET_SALE', saleId: sale.id });
      updateSaleIdRef(sale.id);
      return sale.id;
    } catch {
      toast.error('Erro de conexão ao criar venda');
      return null;
    }
  }, []);

  /**
   * Ensure a sale exists (create if needed).
   * Returns the sale ID or null on failure.
   */
  const ensureSale = useCallback(async (): Promise<string | null> => {
    if (saleIdRef.current) return saleIdRef.current;
    return await createSale();
  }, [createSale]);

  /**
   * Load an existing open sale from the backend (resume interrupted sale).
   */
  const loadExistingSale = useCallback(async (): Promise<boolean> => {
    dispatch({ type: 'SET_LOADING', loading: true });
    try {
      const response = await apiFetch('/api/sales/current');
      if (response.status === 404) {
        // No open sale - that's fine
        dispatch({ type: 'SET_LOADING', loading: false });
        return false;
      }
      if (!response.ok) {
        dispatch({ type: 'SET_LOADING', loading: false });
        return false;
      }

      const sale = await response.json();
      const items: SaleItem[] = (sale.items || []).map((item: Record<string, unknown>) => ({
        id: item.id as string,
        product_id: item.product_id as string,
        product_name: (item.product_name as string) || 'Produto',
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
        unit: (item.unit as string) || 'un',
        subtotal: Number(item.subtotal),
      }));

      dispatch({
        type: 'LOAD_SALE',
        state: {
          saleId: sale.id,
          items,
          discount: Number(sale.desconto) || 0,
          customer: sale.customer_id ? { id: sale.customer_id, name: '' } : null,
        },
      });
      updateSaleIdRef(sale.id);
      dispatch({ type: 'SET_LOADING', loading: false });

      if (items.length > 0) {
        toast.info('Venda em andamento recuperada');
      }
      return true;
    } catch {
      dispatch({ type: 'SET_LOADING', loading: false });
      return false;
    }
  }, []);

  /**
   * Add an item to the sale.
   * Optimistically updates local state, then syncs with backend.
   * Uses the backend-returned item ID for future operations.
   */
  const addItem = useCallback(
    async (item: SaleItem) => {
      // Optimistic local update
      dispatch({ type: 'ADD_ITEM', item });

      // Ensure sale exists
      const saleId = await ensureSale();
      if (!saleId) {
        // Sale creation failed - item is still in local state for UX
        return;
      }

      // Sync with backend
      try {
        const response = await apiFetch(`/api/sales/${saleId}/items`, {
          method: 'POST',
          body: JSON.stringify({
            product_id: item.product_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            unit: item.unit,
            product_name: item.product_name,
          }),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: 'Erro ao salvar item' }));
          toast.error(err.error || 'Erro ao salvar item no servidor');
          return;
        }

        const data = await response.json();
        // Update the local item with the backend ID so future updates/deletes work
        const backendItem = data.item;
        if (backendItem && backendItem.id) {
          dispatch({
            type: 'UPDATE_ITEM',
            itemId: item.id,
            newItem: {
              id: backendItem.id,
              product_id: backendItem.product_id,
              product_name: backendItem.product_name || item.product_name,
              quantity: Number(backendItem.quantity),
              unit_price: Number(backendItem.unit_price),
              unit: backendItem.unit || item.unit,
              subtotal: Number(backendItem.subtotal),
            },
          });
        }
      } catch {
        toast.error('Erro de conexão ao salvar item');
      }
    },
    [ensureSale],
  );

  /**
   * Update item quantity. Syncs with backend.
   */
  const updateItemQuantity = useCallback(
    async (itemId: string, quantity: number) => {
      // Optimistic local update
      dispatch({ type: 'UPDATE_ITEM_QUANTITY', itemId, quantity });

      const saleId = saleIdRef.current;
      if (!saleId) return;

      try {
        const response = await apiFetch(`/api/sales/${saleId}/items/${itemId}`, {
          method: 'PATCH',
          body: JSON.stringify({ quantity }),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: 'Erro ao atualizar item' }));
          toast.error(err.error || 'Erro ao atualizar item no servidor');
        }
      } catch {
        toast.error('Erro de conexão ao atualizar item');
      }
    },
    [],
  );

  /**
   * Remove an item from the sale. Syncs with backend.
   */
  const removeItem = useCallback(
    async (itemId: string) => {
      // Optimistic local update
      dispatch({ type: 'REMOVE_ITEM', itemId });

      const saleId = saleIdRef.current;
      if (!saleId) return;

      try {
        const response = await apiFetch(`/api/sales/${saleId}/items/${itemId}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({ error: 'Erro ao remover item' }));
          toast.error(err.error || 'Erro ao remover item do servidor');
        }
      } catch {
        toast.error('Erro de conexão ao remover item');
      }
    },
    [],
  );

  const setDiscount = useCallback((discount: number) => {
    dispatch({ type: 'SET_DISCOUNT', discount });
  }, []);

  const setCustomer = useCallback((customer: { id: string; name: string } | null) => {
    dispatch({ type: 'SET_CUSTOMER', customer });
  }, []);

  const clearSale = useCallback(() => {
    dispatch({ type: 'CLEAR_SALE' });
    updateSaleIdRef(null);
  }, []);

  const loadSale = useCallback((saleState: Partial<PdvState>) => {
    dispatch({ type: 'LOAD_SALE', state: saleState });
    if (saleState.saleId) {
      updateSaleIdRef(saleState.saleId);
    }
  }, []);

  return {
    state,
    addItem,
    updateItemQuantity,
    removeItem,
    setDiscount,
    setCustomer,
    clearSale,
    loadSale,
    loadExistingSale,
    ensureSale,
  };
}
